import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { getTeamIdFromPlayer } from '../../utils/player';
import { isValidHttpUrl } from '../../utils/url';

function TeamSheet({
    teams,
    predictionAnswers = {},
    predictionsRevealed,
    activeTeamId,
    allPlayers,
    showPickBudget = false,
    besserwisserState = null,
    quizpokerState = null,   // Quizpoker-Finale-State (wenn aktiv)
    finalfrageState = null,  // Finalfrage-Finale-State (wenn aktiv)
    yourTeamId = null,
    myTeamJoinCode = null,
}) {
    const [isExpanded, setIsExpanded] = useState(true);
    const [bodyHeight, setBodyHeight] = useState(0);
    const toggleRef = useRef(null);
    const contentRef = useRef(null);

    const toggle = () => setIsExpanded((prev) => !prev);

    // Bei "Finalfrage": Teamleiste einklappen, sobald das Ergebnis angezeigt wird.
    const ffResolved = finalfrageState?.live?.phase === 'resolved';
    useEffect(() => {
        if (ffResolved) setIsExpanded(false);
    }, [ffResolved]);

    const playersWithoutTeam = useMemo(
        () => allPlayers.filter((p) => !getTeamIdFromPlayer(p)),
        [allPlayers]
    );

    const runnerUpTeamIds = useMemo(() => {
        if (!teams.length) return new Set();
        const scores = teams.map((team) => team.punkte ?? 0);
        const uniqueScores = new Set(scores);
        if (uniqueScores.size === 1) return new Set();
        const maxScore = Math.max(...scores);
        const secondScores = scores.filter((score) => score < maxScore);
        if (!secondScores.length) return new Set();
        const secondBest = Math.max(...secondScores);
        const secondPlaceTeams = teams.filter((team) => (team.punkte ?? 0) === secondBest);
        return new Set(secondPlaceTeams.map((team) => team.id));
    }, [teams]);

    // Showdown-Leader: vom Server berechnet (live.showdownLeaderIds), nach resolve aus result
    const showdownLeaderIds = useMemo(() => {
        if (!quizpokerState?.live) return new Set();
        const live = quizpokerState.live;
        const phase = live.phase;
        if (phase !== 'showdown' && phase !== 'resolved') return new Set();
        // Nach resolve: Endsieger aus result
        if (phase === 'resolved' && Array.isArray(live.result?.winnerTeamIds)) {
            return new Set(live.result.winnerTeamIds.map(String));
        }
        // Showdown: live vom Server berechnete Führende
        if (Array.isArray(live.showdownLeaderIds) && live.showdownLeaderIds.length > 0) {
            return new Set(live.showdownLeaderIds.map(String));
        }
        return new Set();
    }, [quizpokerState]);

    const calculateBodyHeight = useCallback(() => {
        if (!contentRef.current) return;
        const contentHeight = contentRef.current.scrollHeight;
        const toggleHeight = toggleRef.current?.offsetHeight || 56;
        const available = Math.max(window.innerHeight - toggleHeight - 32, 240);
        setBodyHeight(Math.min(contentHeight, available));
    }, []);

    useEffect(() => {
        if (isExpanded) calculateBodyHeight();
    }, [teams, allPlayers, isExpanded, calculateBodyHeight, quizpokerState, finalfrageState]);

    useEffect(() => {
        window.addEventListener('resize', calculateBodyHeight);
        return () => window.removeEventListener('resize', calculateBodyHeight);
    }, [calculateBodyHeight]);

    const renderMembers = (team) => {
        const members = team.spieler && team.spieler.length
            ? team.spieler
            : allPlayers.filter((p) => getTeamIdFromPlayer(p) === team.id);
        const safeMembers = members.map((m) => ({
            ...m,
            safeLink: isValidHttpUrl(m?.link) ? m.link.trim() : null,
        }));

        return (
            <div className="team-liveviews">
                {safeMembers.length === 0 && <span className="pill muted">Keine Mitglieder</span>}
                {safeMembers.map((player) => (
                    player.safeLink ? (
                        <div className="liveview-card" key={player.id || player.name}>
                            <div className="liveview-frame">
                                <iframe
                                    src={player.safeLink}
                                    title={`${player.name}-liveview`}
                                    allowFullScreen
                                    scrolling="no"
                                />
                            </div>
                            <div className="liveview-name">{player.name}</div>
                        </div>
                    ) : (
                        <span className="liveview-chip" key={player.id || player.name}>
                            {player.name || 'Unbekannt'}
                        </span>
                    )
                ))}
            </div>
        );
    };

    const renderJokerIcon = (jokerUsedEver) => (
        <span
            className={`team-joker-icon ${jokerUsedEver ? 'used' : 'available'}`}
            aria-label={jokerUsedEver ? 'Joker benutzt' : 'Joker verfügbar'}
            title={jokerUsedEver ? 'Joker benutzt' : 'Joker verfügbar'}
        >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M4.5 20.5 3 19l9.3-9.3 1.5 1.5L4.5 20.5Zm10.2-9.7-1.5-1.5 2-2a2.12 2.12 0 0 1 3 0l.5.5a2.12 2.12 0 0 1 0 3l-2 2ZM5 7l2.2.5L7.7 10l1.5-2.5L12 7l-2.8-.5L7.7 4l-.5 2.5L5 7Zm10 7 1.6.3.3 1.6.3-1.6 1.6-.3-1.6-.3-.3-1.6-.3 1.6-1.6.3Z" />
            </svg>
        </span>
    );

    // Lesbares Label für lastAction
    const formatLastAction = (action) => {
        if (!action) return null;
        switch (action.type) {
            case 'check': return 'Check';
            case 'call': return `Call ${action.amount}`;
            case 'raise': return `Raise ${action.amount}`;
            case 'allin': return `All-In ${action.amount}`;
            case 'fold': return 'Fold';
            case 'ante': return null; // Ante wird nicht angezeigt
            case 'blind':
                if (action.blindRole === 'small') return `Small Blind ${action.amount}`;
                if (action.blindRole === 'big') return `Big Blind ${action.amount}`;
                return `Blind ${action.amount}`;
            default: return null;
        }
    };

    // Quizpoker-Statuszeile für ein Team (ohne "Gewinner"-Chip)
    const renderQpStatus = (team) => {
        if (!quizpokerState?.live) return null;
        const live = quizpokerState.live;
        const qp = live.perTeam?.[String(team.id)];
        if (!qp) return null;

        const phase = live.phase;
        const parts = [];
        const actionLabel = formatLastAction(qp.lastAction);

        // Status-Chips: "Gefoldet" immer; "All-In" nur wenn action-Chip es nicht schon zeigt
        if (qp.status === 'folded') {
            parts.push(<span key="folded" className="qp-team-status qp-status-folded">Gefoldet</span>);
        } else if (qp.status === 'allin' && qp.lastAction?.type !== 'allin') {
            // Nur zeigen wenn der letzte Zug kein All-In war (sonst Dopplung mit Action-Chip)
            parts.push(<span key="allin" className="qp-team-status qp-status-allin">All-In</span>);
        }

        // Schätzrunde: beantwortet?
        if (phase === 'answering') {
            parts.push(
                <span key="answered" className={`qp-team-status ${qp.answered ? 'qp-status-answered' : 'qp-status-waiting'}`}>
                    {qp.answered ? '✓ Geantwortet' : 'Noch offen'}
                </span>
            );
        }

        // Letzter Zug (Action-Chip — enthält z.B. "All-In 50", "Small Blind 10", "Raise 30")
        if (actionLabel && qp.status !== 'folded') {
            parts.push(
                <span key="action" className="qp-team-status qp-status-action">{actionLabel}</span>
            );
        }

        if (!parts.length) return null;
        return <div className="qp-team-statusline">{parts}</div>;
    };

    // ===== Finalfrage =====
    const ffLive = finalfrageState?.live || null;
    const ffCorrectIndex = Number.isInteger(finalfrageState?.question?.correctIndex)
        ? finalfrageState.question.correctIndex
        : -1;

    const ffColorFor = (teamId) => ffLive?.teamColors?.[String(teamId)] || null;

    // Delta +Einsatz / −Einsatz neben dem Score – sobald die Lösung aufgedeckt ist
    // UND der Pick dieses Teams öffentlich ist (wie beim Quizpoker-Showdown).
    // In der Phase 'resolved' (Punkte verrechnet) NICHT mehr anzeigen.
    const ffDeltaFor = (teamId) => {
        if (!ffLive || !ffLive.solutionRevealed || ffLive.phase === 'resolved') return null;
        const entry = ffLive.perTeam?.[String(teamId)];
        if (!entry || !entry.revealed) return null;
        if (entry.pick == null || entry.wager == null) return null;
        return entry.pick === ffCorrectIndex ? entry.wager : -entry.wager;
    };

    // BB/SB-Token neben dem Teamnamen
    const renderBlindToken = (teamId) => {
        const live = quizpokerState?.live;
        if (!live) return null;
        const id = String(teamId);
        if (live.bigBlindTeamId && String(live.bigBlindTeamId) === id) {
            return <span className="qp-blind-token bb">Big Blind</span>;
        }
        if (live.smallBlindTeamId && String(live.smallBlindTeamId) === id) {
            return <span className="qp-blind-token sb">Small Blind</span>;
        }
        return null;
    };

    return (
        <div className={`team-sheet ${isExpanded ? 'expanded' : 'collapsed'}`}>
            <button
                className="team-sheet-toggle"
                aria-label={isExpanded ? 'Teamübersicht einklappen' : 'Teamübersicht ausklappen'}
                onClick={toggle}
                ref={toggleRef}
            >
                <span>Teams</span>
                <span className={`chevron ${isExpanded ? 'up' : 'down'}`}>⌃</span>
            </button>

            <div
                className="team-sheet-body"
                role="region"
                aria-label="Teamübersicht"
                style={{ maxHeight: isExpanded ? bodyHeight : 0, padding: isExpanded ? '0 16px 12px' : '0 16px 0' }}
            >
                <div className="team-sheet-content" ref={contentRef}>
                    <div className={`team-list${quizpokerState?.live ? ' qp-live' : ''}${finalfrageState?.live ? ' ff-live' : ''}`}>
                        {/* handAnswerValue: außerhalb der Map berechnet, damit isQpWinner darauf zugreifen kann */}
                        {(() => {
                        const handAnswerValue = quizpokerState?.currentHand?.answerValue
                            ?? quizpokerState?.hands?.[quizpokerState?.handIndex]?.answerValue;
                        return teams.map((team) => {
                            const predicted = predictionAnswers[team.id];
                            const stateClass = predictionsRevealed
                                ? predicted === true ? 'team-positive'
                                    : predicted === false ? 'team-negative'
                                        : ''
                                : '';
                            const isActive = team.id === activeTeamId;
                            const isRunnerUp = runnerUpTeamIds.has(team.id);

                            // Quizpoker-Klassen
                            const live = quizpokerState?.live;
                            const qp = live?.perTeam?.[String(team.id)];
                            const isFolded = qp?.status === 'folded';
                            const isAllin = qp?.status === 'allin';
                            // Showdown-Leader (goldener Rand) — nur wenn answerValue bekannt (= gleiche Bedingung wie Delta)
                            const isQpWinner = showdownLeaderIds.has(String(team.id)) && handAnswerValue != null;
                            // Aktuell am Zug (Setzrunde) → gleiche Cyan-Markierung wie normales aktives Team
                            const isQpActing = quizpokerState
                                && live?.phase === 'betting'
                                && live?.actingTeamId != null
                                && String(live.actingTeamId) === String(team.id)
                                && !isFolded;

                            // Finalfrage
                            const ffColor = ffColorFor(team.id);
                            const ffDelta = ffDeltaFor(team.id);
                            const isFfWinner = !!finalfrageState
                                && ffLive?.phase === 'resolved'
                                && (ffLive.result?.winnerTeamIds || []).map(String).includes(String(team.id));

                            const classNames = [
                                'team-row',
                                isActive && !quizpokerState && !finalfrageState && 'active',
                                stateClass,
                                isRunnerUp && !quizpokerState && !finalfrageState && 'runner-up',
                                quizpokerState && isFolded && 'qp-folded',
                                quizpokerState && isAllin && 'qp-allin',
                                quizpokerState && isQpWinner && 'qp-winner',
                                isQpActing && 'qp-acting',
                                finalfrageState && 'ff-row',
                                isFfWinner && 'qp-winner',
                            ]
                                .filter(Boolean)
                                .join(' ');

                            const pickBudgetValue = Number.isFinite(team.pickBudget) ? team.pickBudget : 0;
                            const jokerUsed = Boolean(besserwisserState?.jokerUsedByTeamId?.[team.id]);

                            // Showdown-State: aufgedeckte Schätzung groß anzeigen
                            const phase = live?.phase;
                            const isShowdownRevealed = !!quizpokerState
                                && (phase === 'showdown' || phase === 'resolved')
                                && qp?.revealed
                                && qp?.answer != null;
                            // handAnswerValue von außen (oben berechnet) — für Moderator immer bekannt,
                            // für Spieler erst nach solutionRevealed (weil der Wert dann im Payload mitkommt).
                            // Vorzeichenbehaftetes Delta: sofort anzeigen wenn answerValue bekannt.
                            const delta = isShowdownRevealed && handAnswerValue != null
                                ? qp.answer - handAnswerValue
                                : null;
                            // Nettopunktzahl (nach Abzug des Einsatzes), im resolved-State direkt punkte
                            const committed = qp?.committedTotal ?? 0;
                            const netScore = phase === 'resolved' ? team.punkte : team.punkte - committed;
                            // Pot-Anteil für Gewinner: nur in resolved-Phase anzeigen
                            const payoutGain = phase === 'resolved'
                                ? (live?.payoutDisplay?.[String(team.id)] ?? 0)
                                : 0;

                            return (
                                <div key={team.id} className={classNames}>
                                    {ffColor && (
                                        <span className="ff-color-bar" style={{ background: ffColor }} aria-hidden="true" />
                                    )}
                                    {isShowdownRevealed ? (
                                        // Showdown-Layout: Schätzung groß, Delta klein rechts daneben
                                        <div className="team-row-showdown">
                                            <div className="team-row-showdown-header">
                                                <span className="team-name">{team.name}</span>
                                                <span className="team-score">
                                                    {netScore}
                                                    {payoutGain > 0 && (
                                                        <span className="qp-payout-gain"> +{payoutGain}</span>
                                                    )}
                                                </span>
                                            </div>
                                            <div className="team-row-showdown-guess-row">
                                                <span className="team-row-showdown-guess">{qp.answer}</span>
                                                {delta != null && (
                                                    <span className={`team-row-showdown-delta-inline ${
                                                        delta === 0 ? 'delta-zero'
                                                        : delta > 0 ? 'delta-over'
                                                        : 'delta-under'
                                                    }`}>
                                                        {delta === 0 ? '±0' : delta > 0 ? `+${delta}` : `${delta}`}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        // Normal-Layout (answering, betting)
                                        <div className="team-row-main">
                                            <div className="team-row-details">
                                                <div className="team-name-row">
                                                    <span className="team-name-group">
                                                        <span className="team-name">{team.name}</span>
                                                        {!quizpokerState && !finalfrageState && renderJokerIcon(jokerUsed)}
                                                        {quizpokerState && renderBlindToken(team.id)}
                                                        {String(team.id) === String(yourTeamId) && myTeamJoinCode && (
                                                            <span className="team-join-code" title="Nur fuer euer Team sichtbar">
                                                                Code: {myTeamJoinCode}
                                                            </span>
                                                        )}
                                                    </span>
                                                </div>
                                                {renderQpStatus(team)}
                                                {renderMembers(team)}
                                            </div>
                                            <div className="team-score">
                                                {quizpokerState ? netScore : team.punkte}
                                                {ffDelta != null && (
                                                    <span className={`team-row-showdown-delta-inline ${
                                                        ffDelta === 0 ? 'delta-zero'
                                                        : ffDelta > 0 ? 'delta-over'
                                                        : 'delta-under'
                                                    }`}>
                                                        {ffDelta === 0 ? '±0' : ffDelta > 0 ? ` +${ffDelta}` : ` ${ffDelta}`}
                                                    </span>
                                                )}
                                                {showPickBudget && !quizpokerState && !finalfrageState && (
                                                    <span className="team-pickbudget"> | {pickBudgetValue}</span>
                                                )}
                                                {quizpokerState?.live && phase !== 'resolved' && committed > 0 && (
                                                    <span className="team-pickbudget qp-score-bet"> | {committed}</span>
                                                )}
                                                {payoutGain > 0 && (
                                                    <span className="qp-payout-gain"> +{payoutGain}</span>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        });
                        })()}
                    </div>

                    {playersWithoutTeam.length > 0 && (() => {
                        const fallbackMembers = playersWithoutTeam.map((p) => ({
                            ...p,
                            safeLink: isValidHttpUrl(p?.link) ? p.link.trim() : null,
                        }));
                        return (
                            <div className="no-team">
                                <div className="team-name-row">
                                    <span className="team-name">Ohne Team</span>
                                    <span className="pill muted">{playersWithoutTeam.length}</span>
                                </div>
                                <div className="team-liveviews">
                                    {fallbackMembers.map((player) => (
                                        player.safeLink ? (
                                            <div className="liveview-card" key={player.id || player.name}>
                                                <div className="liveview-frame">
                                                    <iframe
                                                        src={player.safeLink}
                                                        title={`${player.name}-liveview`}
                                                        allowFullScreen
                                                        scrolling="no"
                                                    />
                                                </div>
                                                <div className="liveview-name">{player.name}</div>
                                            </div>
                                        ) : (
                                            <span className="liveview-chip" key={player.id || player.name}>
                                                {player.name || 'Unbekannt'}
                                            </span>
                                        )
                                    ))}
                                </div>
                            </div>
                        );
                    })()}
                </div>
            </div>
        </div>
    );
}

export default TeamSheet;
